import { useEffect, useMemo, useState } from "react";
import { MapContainer, GeoJSON, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Users, Inbox, BookOpen, Globe2, Compass, Target, Settings2 } from "lucide-react";

// Counties HarborWell serves today (mirrored from backend HARBORWELL_SERVED_COUNTIES)
const SERVED = ["Wayne", "Lenoir", "Pitt", "Greene", "Wilson"];
const EASTERN_NC_FOCUS = new Set([
  // Region 9 + Region 10 + adjacent — Eastern NC the team realistically reaches
  "Wayne", "Lenoir", "Pitt", "Greene", "Wilson", "Edgecombe", "Nash", "Johnston",
  "Sampson", "Duplin", "Jones", "Craven", "Pamlico", "Carteret", "Onslow",
  "Beaufort", "Martin", "Halifax", "Northampton", "Bertie", "Hertford",
  "Gates", "Pasquotank", "Camden", "Currituck", "Chowan", "Perquimans",
  "Tyrrell", "Washington", "Dare", "Hyde", "Pender", "New Hanover", "Brunswick",
  "Columbus", "Bladen", "Robeson", "Hoke", "Cumberland", "Harnett", "Wake",
  "Franklin", "Vance", "Warren", "Granville",
]);

const METRICS = [
  { key: "care_seekers",    label: "Care Seekers",     icon: Users,    color: "#1E3A8A" },
  { key: "intake_requests", label: "Inbound demand",   icon: Inbox,    color: "#B45309" },
  { key: "resources",       label: "Resources avail.", icon: BookOpen, color: "#7E937D" },
  { key: "goal_pct",        label: "Goal progress",    icon: Target,   color: "#166534" },
];

export default function GeoGap() {
  const [scope, setScope] = useState("eastern"); // 'eastern' | 'statewide'
  const [metric, setMetric] = useState("care_seekers");
  const [data, setData] = useState(null);
  const [geo, setGeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGoalsDrawer, setShowGoalsDrawer] = useState(false);

  const reload = () =>
    api.get("/reports/geo-gap").then(({ data }) => setData(data));

  useEffect(() => {
    Promise.all([
      api.get("/reports/geo-gap"),
      fetch("/data/nc-counties.geojson").then((r) => r.json()),
    ])
      .then(([statsRes, geoRes]) => {
        setData(statsRes.data);
        setGeo(geoRes);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  // Map county name → row
  const byCounty = useMemo(() => {
    const m = new Map();
    (data?.counties || []).forEach((c) => m.set(c.county.toLowerCase(), c));
    return m;
  }, [data]);

  // Compute max for the active metric so color scale is data-driven
  const maxVal = useMemo(() => {
    if (!data) return 1;
    if (metric === "goal_pct") return 100;
    return Math.max(
      1,
      ...data.counties.map((c) => c[metric] || 0)
    );
  }, [data, metric]);

  const fillFor = (countyName) => {
    const row = byCounty.get((countyName || "").toLowerCase());
    if (!row) return "#F1F5F9";
    // Goal progress: counties with no goal stay neutral; with a goal, shade by % to goal
    if (metric === "goal_pct") {
      if (row.target_care_seekers === 0) return "#F1F5F9";
      const intensity = Math.min(1, (row.goal_pct || 0) / 100);
      // White → green gradient (#F0FDF4 → #166534)
      const r1 = 240, g1 = 253, b1 = 244, r2 = 22, g2 = 101, b2 = 52;
      const r = Math.round(r1 + (r2 - r1) * intensity);
      const g = Math.round(g1 + (g2 - g1) * intensity);
      const b = Math.round(b1 + (b2 - b1) * intensity);
      return `rgb(${r},${g},${b})`;
    }
    const val = row[metric] || 0;
    if (!val) return "#F1F5F9";
    const intensity = Math.min(1, val / maxVal);
    const palette = {
      care_seekers: [219, 234, 254, 30, 58, 138],
      intake_requests: [254, 215, 170, 180, 83, 9],
      resources: [220, 238, 217, 126, 147, 125],
    }[metric];
    const [r1, g1, b1, r2, g2, b2] = palette;
    const r = Math.round(r1 + (r2 - r1) * intensity);
    const g = Math.round(g1 + (g2 - g1) * intensity);
    const b = Math.round(b1 + (b2 - b1) * intensity);
    return `rgb(${r},${g},${b})`;
  };

  const styleFeature = (feat) => {
    const name = feat.properties.NAME;
    const isServed = SERVED.includes(name);
    const inEastern = EASTERN_NC_FOCUS.has(name);
    const dimmed = scope === "eastern" && !inEastern;
    return {
      fillColor: dimmed ? "#F8FAFC" : fillFor(name),
      fillOpacity: dimmed ? 0.25 : 0.85,
      color: isServed ? "#1E3A8A" : "#94A3B8",
      weight: isServed ? 2.5 : 0.6,
      opacity: dimmed ? 0.35 : 1,
    };
  };

  const onEachFeature = (feature, layer) => {
    const name = feature.properties.NAME;
    const row = byCounty.get(name.toLowerCase());
    const cs = row?.care_seekers || 0;
    const ir = row?.intake_requests || 0;
    const rs = row?.resources || 0;
    const target = row?.target_care_seekers || 0;
    const pct = row?.goal_pct;
    const isServed = SERVED.includes(name);
    layer.bindTooltip(
      `<div style="font-family:sans-serif;font-size:12px;line-height:1.4;">
        <div style="font-weight:600;color:#0F172A;">${name} County${isServed ? " · <span style='color:#1E3A8A'>Served</span>" : ""}</div>
        <div style="color:#334155;">Care Seekers: <b>${cs}</b>${target ? ` / <b>${target}</b> goal (${pct}%)` : ""}</div>
        <div style="color:#334155;">Inbound demand: <b>${ir}</b></div>
        <div style="color:#334155;">Resources: <b>${rs}</b></div>
      </div>`,
      { sticky: true, direction: "auto" }
    );
  };

  // Map center & zoom
  const center = scope === "eastern" ? [35.4, -77.8] : [35.6, -79.4];
  const zoom = scope === "eastern" ? 7 : 7;

  // Ranked gap lists
  const ranked = useMemo(() => {
    if (!data) return { coverage: [], demand: [], resource: [], goal: [] };
    const inScope = (c) =>
      scope === "statewide" ? true : EASTERN_NC_FOCUS.has(c.county);
    const counties = (data.counties || []).filter(inScope);
    return {
      coverage: counties.filter((c) => c.coverage_gap).sort((a, b) => b.intake_requests - a.intake_requests),
      demand:   counties.filter((c) => c.demand_gap > 0).sort((a, b) => b.demand_gap - a.demand_gap),
      resource: counties.filter((c) => c.resource_gap).sort((a, b) => b.care_seekers - a.care_seekers),
      goal:     counties
        .filter((c) => c.target_care_seekers > 0 && (c.goal_pct || 0) < 100)
        .sort((a, b) => (a.goal_pct || 0) - (b.goal_pct || 0)),
    };
  }, [data, scope]);

  return (
    <div className="space-y-6" data-testid="geo-gap-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Insight</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
            Geographic gap analysis
          </h1>
          <p className="text-sm text-[#334155] mt-2 max-w-2xl">
            See where HarborWell has coverage, where demand outstrips capacity,
            and where the Resources directory is thin. Counties outlined in
            navy are the ones HarborWell officially serves today
            ({SERVED.join(", ")}).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGoalsDrawer(true)}
            data-testid="manage-goals-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#334155] text-xs font-medium hover:bg-[#F9F8F6]"
          >
            <Settings2 className="w-3.5 h-3.5" /> Manage goals
          </button>
          <button
            onClick={() => setScope("eastern")}
            data-testid="scope-eastern"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
              scope === "eastern"
                ? "bg-[#1E3A8A] text-white"
                : "bg-white border border-[#E5E5E0] text-[#334155]"
            }`}
          >
            <Compass className="w-3.5 h-3.5" /> Eastern NC
          </button>
          <button
            onClick={() => setScope("statewide")}
            data-testid="scope-statewide"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
              scope === "statewide"
                ? "bg-[#1E3A8A] text-white"
                : "bg-white border border-[#E5E5E0] text-[#334155]"
            }`}
          >
            <Globe2 className="w-3.5 h-3.5" /> Statewide
          </button>
        </div>
      </div>

      {/* Metric toggle chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="metric-toggle">
        <span className="text-xs uppercase tracking-wider text-[#1E3A8A] font-semibold mr-2">
          Shade by:
        </span>
        {METRICS.map((m) => {
          const Icon = m.icon;
          const active = metric === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setMetric(m.key)}
              data-testid={`metric-${m.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? "border-[#1E3A8A] bg-[#E0E7FF] text-[#1E3A8A]"
                  : "border-[#E5E5E0] bg-white text-[#334155] hover:bg-[#F9F8F6]"
              }`}
              style={active ? { borderColor: m.color, color: m.color } : {}}
            >
              <Icon className="w-3.5 h-3.5" /> {m.label}
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[1fr,360px] gap-6">
        {/* Map */}
        <div
          className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden"
          data-testid="geo-map-card"
        >
          <div className="h-[540px] relative">
            {loading || !geo ? (
              <div className="absolute inset-0 flex items-center justify-center text-[#1E3A8A] text-sm">
                Loading map…
              </div>
            ) : (
              <MapContainer
                key={scope}
                center={center}
                zoom={zoom}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={false}
                attributionControl={false}
              >
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  subdomains="abcd"
                />
                <GeoJSON
                  key={metric + scope}
                  data={geo}
                  style={styleFeature}
                  onEachFeature={onEachFeature}
                />
              </MapContainer>
            )}
          </div>
          <div className="px-4 py-3 border-t border-[#E5E5E0] flex items-center justify-between text-xs text-[#334155]">
            <div>
              <span className="inline-block w-3 h-3 rounded-sm bg-[#1E3A8A] mr-1.5 align-middle"></span>
              HarborWell-served counties (outlined)
            </div>
            <div className="text-[#7E937D]">
              Hover any county for full breakdown
            </div>
          </div>
        </div>

        {/* Gap lists */}
        <div className="space-y-4" data-testid="gap-lists">
          <GapList
            title="Coverage gaps"
            tooltip="Counties HarborWell serves but has 0 Care Seekers — or counties with inbound demand but no Care Seekers."
            color="#1E3A8A"
            icon={Users}
            items={ranked.coverage}
            empty="No coverage gaps in this scope."
            render={(c) => (
              <span className="text-[11px] text-[#334155]">
                {c.intake_requests} inbound · {c.resources} resources
              </span>
            )}
            testId="gap-coverage"
          />
          <GapList
            title="Demand gaps"
            tooltip="Counties where /refer submissions exceed Care Seekers actually onboarded — worth following up on."
            color="#B45309"
            icon={Inbox}
            items={ranked.demand}
            empty="No demand gaps."
            render={(c) => (
              <span className="text-[11px] font-semibold text-[#B45309]">
                +{c.demand_gap}
              </span>
            )}
            testId="gap-demand"
          />
          <GapList
            title="Resource gaps"
            tooltip="Counties with Care Seekers but no entries in the Resources directory — likely under-referring."
            color="#7E937D"
            icon={BookOpen}
            items={ranked.resource}
            empty="No resource gaps."
            render={(c) => (
              <span className="text-[11px] text-[#334155]">
                {c.care_seekers} Care Seekers · 0 resources
              </span>
            )}
            testId="gap-resource"
          />
          <GapList
            title="Goal progress"
            tooltip="Counties with an outreach goal set but not yet at 100% of target Care Seekers for the year."
            color="#166534"
            icon={Target}
            items={ranked.goal}
            empty={`No active goals yet. Click "Manage goals" to set yearly targets per county.`}
            render={(c) => (
              <span className="text-[11px] font-semibold text-[#166534]">
                {c.care_seekers}/{c.target_care_seekers} ({c.goal_pct}%)
              </span>
            )}
            testId="gap-goal"
          />

          {data && (
            <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-2xl p-4 text-xs text-[#334155]" data-testid="geo-totals">
              <div className="font-semibold text-[#1E3A8A] uppercase tracking-wider text-[10px] mb-2">
                Totals · across {data.totals.counties_with_data} counties · {data.totals.counties_with_goals || 0} with goals
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Care Seekers" value={data.totals.care_seekers} />
                <Stat label="Inbound" value={data.totals.intake_requests} />
                <Stat label="Resources" value={data.totals.resources} />
              </div>
            </div>
          )}
        </div>
      </div>

      {showGoalsDrawer && (
        <ManageGoalsDrawer
          year={data?.year || new Date().getFullYear()}
          onClose={() => setShowGoalsDrawer(false)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

function ManageGoalsDrawer({ year, onClose, onChanged }) {
  const [goals, setGoals] = useState(null);
  const [newCounty, setNewCounty] = useState("");
  const [newTarget, setNewTarget] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/outreach-goals", { params: { year } }).then(({ data }) => setGoals(data || []));

  useEffect(() => {
    load();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const save = async (e) => {
    e.preventDefault();
    const target = parseInt(newTarget, 10);
    if (!newCounty.trim() || !target || target < 1) {
      toast.error("Enter a county and a target number greater than zero.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/outreach-goals", {
        county: newCounty.trim(),
        year,
        target_care_seekers: target,
      });
      setNewCounty(""); setNewTarget("");
      await load();
      onChanged();
      toast.success("Goal saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this goal?")) return;
    try {
      await api.delete(`/outreach-goals/${id}`);
      await load();
      onChanged();
      toast.success("Goal removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-[#0F172A]/40" />
      <div
        className="relative bg-white w-full max-w-md h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="goals-drawer"
      >
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">Per-county targets</div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">
              Outreach goals — {year}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#F9F8F6]" data-testid="goals-close">
            <Settings2 className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={save} className="p-5 space-y-3 border-b border-[#E5E5E0]" data-testid="goals-add-form">
          <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">
            Add or update a county goal
          </div>
          <div className="flex gap-2">
            <input
              value={newCounty}
              onChange={(e) => setNewCounty(e.target.value)}
              placeholder="County (e.g. Wayne)"
              data-testid="goal-county-input"
              className="flex-1 px-3 py-2 rounded-xl border border-[#E5E5E0] text-sm"
            />
            <input
              type="number"
              min={1}
              value={newTarget}
              onChange={(e) => setNewTarget(e.target.value)}
              placeholder="Target"
              data-testid="goal-target-input"
              className="w-24 px-3 py-2 rounded-xl border border-[#E5E5E0] text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="goal-save-btn"
            className="w-full py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save goal"}
          </button>
          <p className="text-[11px] text-[#7E937D]">
            Adding a goal for an existing county updates the target. Goals
            are yearly Care Seeker counts for {year}.
          </p>
        </form>

        <div className="p-5">
          <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider mb-2">
            Current goals
          </div>
          {goals === null ? (
            <div className="text-sm text-[#1E3A8A]">Loading…</div>
          ) : goals.length === 0 ? (
            <div className="text-sm text-[#7E937D] italic" data-testid="goals-empty">
              No goals set for {year} yet.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="goals-list">
              {goals.sort((a, b) => a.county.localeCompare(b.county)).map((g) => (
                <li
                  key={g.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-[#E5E5E0]"
                  data-testid={`goal-row-${g.county}`}
                >
                  <div>
                    <div className="text-sm font-medium text-[#0F172A]">{g.county}</div>
                    <div className="text-[11px] text-[#334155]">
                      Target: <span className="font-semibold">{g.target_care_seekers}</span> Care Seekers
                    </div>
                  </div>
                  <button
                    onClick={() => remove(g.id)}
                    data-testid={`goal-delete-${g.county}`}
                    className="text-xs text-[#991B1B] hover:underline"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function GapList({ title, tooltip, color, icon: Icon, items, empty, render, testId }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1a`, color }}>
          <Icon className="w-3.5 h-3.5" strokeWidth={2} />
        </div>
        <h3 className="font-display text-sm font-semibold text-[#0F172A]" title={tooltip}>
          {title}
        </h3>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-[#7E937D]">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#7E937D] italic">{empty}</div>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 6).map((c) => (
            <li
              key={c.county}
              className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-[#F9F8F6]"
              data-testid={`${testId}-row-${c.county}`}
            >
              <span className="text-xs font-medium text-[#0F172A] inline-flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-[#1E3A8A]" />
                {c.county}
                {c.is_served && (
                  <span className="text-[9px] bg-[#E0E7FF] text-[#1E3A8A] px-1.5 rounded-full">
                    Served
                  </span>
                )}
              </span>
              {render(c)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-lg font-semibold text-[#0F172A]">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#7E937D]">{label}</div>
    </div>
  );
}
